import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, MessageCircle, Users, Send, Bot, Ticket, Shield, BarChart3,
  Plus, Search, Filter, MoreVertical, Check, X, Clock, Eye, Phone,
  Mail, Tag, Trash2, Edit2, Play, Pause, ChevronRight, QrCode, Wifi,
  WifiOff, RefreshCw, Upload, Download, Settings, Zap, MessageSquare,
  UserPlus, Inbox, Archive, Star, AlertCircle, CheckCircle2, XCircle,
  ChevronDown, Copy, ExternalLink, FileText, Image, Video, Mic
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { groupTemplatesByFunnel } from '../utils/marketingFunnels';

const whatsappApiPath = (path) => {
  const api = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const normalized = path.startsWith('/api') ? path : `/api/${path.replace(/^\//, '')}`;
  return api ? `${api}${normalized}` : normalized;
};

const qrImageSrc = (qrValue) => {
  if (!qrValue) return null;
  const raw = String(qrValue).trim();
  if (raw.startsWith('data:image') || raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }
  return `data:image/png;base64,${raw.replace(/\s/g, '')}`;
};

// Reusable Components
const StatCard = ({ icon: Icon, label, value, subValue, color = "violet" }) => (
  <div className="glass p-4 rounded-xl">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg bg-${color}-500/20`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-zinc-400">{label}</p>
        {subValue && <p className="text-xs text-green-400">{subValue}</p>}
      </div>
    </div>
  </div>
);

const TabButton = ({ active, onClick, icon: Icon, label, badge }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
      active ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:bg-white/5'
    }`}
  >
    <Icon className="w-4 h-4" />
    <span className="text-sm font-medium">{label}</span>
    {badge > 0 && (
      <span className="px-2 py-0.5 text-xs bg-red-500 rounded-full">{badge}</span>
    )}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action, onAction }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="p-4 rounded-full bg-violet-500/20 mb-4">
      <Icon className="w-8 h-8 text-violet-400" />
    </div>
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <p className="text-sm text-zinc-400 mb-4 max-w-sm">{description}</p>
    {action && (
      <Button onClick={onAction} className="gap-2">
        <Plus className="w-4 h-4" />
        {action}
      </Button>
    )}
  </div>
);

const WhatsAppMarketingDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [chatbots, setChatbots] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [warmer, setWarmer] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState('');
  const [websitesLoading, setWebsitesLoading] = useState(false);

  // UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [settingsPhone, setSettingsPhone] = useState('');
  const [settingsDisplayName, setSettingsDisplayName] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const token = localStorage.getItem('token');

  const loadWebsites = useCallback(async () => {
    if (!token) return;
    setWebsitesLoading(true);
    try {
      const res = await fetch(whatsappApiPath('/websites'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load websites');
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.websites || []);
      setWebsites(list.map((w) => ({
        ...w,
        name: w.name || w.website_name || w.slug || w.custom_domain || 'Untitled website',
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setWebsitesLoading(false);
    }
  }, [token]);

  const contactsQuery = selectedWebsiteId
    ? `?website_id=${encodeURIComponent(selectedWebsiteId)}`
    : '';

  const fetchData = useCallback(async (endpoint) => {
    if (!token) throw new Error('Please log in to use WhatsApp Marketing');
    const res = await fetch(whatsappApiPath(`/whatsapp/${endpoint}`), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      let detail = `Failed to fetch ${endpoint}`;
      try {
        const e = await res.json();
        if (e.detail) detail = typeof e.detail === 'string' ? e.detail : JSON.stringify(e.detail);
      } catch (_) {}
      if (res.status === 401) detail = 'Session expired — please log in again';
      throw new Error(detail);
    }
    return res.json();
  }, [token]);

  const postData = useCallback(async (endpoint, data) => {
    if (!token) throw new Error('Please log in to use WhatsApp Marketing');
    const res = await fetch(whatsappApiPath(`/whatsapp/${endpoint}`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      let detail = `Failed to post to ${endpoint} (HTTP ${res.status})`;
      try {
        const e = await res.json();
        if (e.detail) {
          detail = typeof e.detail === 'string' ? e.detail : JSON.stringify(e.detail);
        } else if (e.message) {
          detail = typeof e.message === 'string' ? e.message : JSON.stringify(e.message);
        } else if (e.error) {
          detail = typeof e.error === 'string' ? e.error : JSON.stringify(e.error);
        }
      } catch (_) {
        try {
          const text = await res.text();
          if (text && text.length < 300) detail = `${detail}: ${text}`;
        } catch (_e2) {}
      }
      if (res.status === 401) detail = 'Session expired — please log in again';
      throw new Error(detail);
    }
    return res.json();
  }, [token]);

  const deleteData = useCallback(async (endpoint) => {
    if (!token) throw new Error('Please log in to use WhatsApp Marketing');
    const res = await fetch(whatsappApiPath(`/whatsapp/${endpoint}`), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to delete ${endpoint}`);
    return res.json();
  }, [token]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const allowed = ['dashboard', 'settings', 'contacts', 'chats', 'broadcasts', 'chatbots', 'tickets', 'templates', 'warmer', 'api'];
    if (tab && allowed.includes(tab)) {
      setActiveTab(tab);
    }
    const broadcastFromUrl = searchParams.get('broadcast');
    if (broadcastFromUrl) {
      setActiveTab('broadcasts');
      window.setTimeout(() => {
        const el = document.querySelector(`[data-broadcast-id="${CSS.escape(broadcastFromUrl)}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-emerald-400');
        }
      }, 500);
    }
  }, [searchParams]);

  useEffect(() => {
    loadWebsites();
  }, [loadWebsites]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!token) {
        setLoading(false);
        setError('Please log in to use WhatsApp Marketing');
        setAccount({
          status: 'disconnected',
          display_name: 'My WhatsApp',
          phone_number: '',
          provider: 'auto',
        });
        return;
      }
      setLoading(true);
      try {
        const accountData = await fetchData('account');
        setAccount(accountData);
        setSettingsPhone(accountData.phone_number || '');
        setSettingsDisplayName(accountData.display_name || '');
      } catch (err) {
        setError(err.message);
        setAccount((prev) => prev || {
          status: 'disconnected',
          display_name: 'My WhatsApp',
          phone_number: '',
          provider: 'auto',
        });
      }
      try {
        const analyticsData = await fetchData('analytics?days=7');
        setAnalytics(analyticsData);
      } catch (_) {
        /* analytics is optional */
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [fetchData]);

  // Auto-detect Evolution QR scan (poll while waiting for connection)
  useEffect(() => {
    if (!account || account.status !== 'connecting' || account.provider !== 'evolution') return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const result = await postData('account/confirm-connection', {});
        if (cancelled) return;
        if (result.status === 'connected') {
          setAccount((prev) => ({ ...prev, status: 'connected', qr_code: null }));
          setError(null);
        }
      } catch (_) {
        /* keep polling */
      }
    };
    const interval = setInterval(poll, 4000);
    poll();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [account?.status, account?.provider, postData]);

  useEffect(() => {
    const loadTabData = async () => {
      try {
        switch (activeTab) {
          case 'contacts':
            const contactsData = await fetchData(`contacts${contactsQuery}`);
            setContacts(contactsData.contacts || []);
            const groupsData = await fetchData('groups');
            setGroups(groupsData || []);
            break;
          case 'chats':
            const convData = await fetchData('conversations');
            setConversations(convData || []);
            break;
          case 'broadcasts':
            const broadcastsData = await fetchData('broadcasts');
            setBroadcasts(broadcastsData || []);
            break;
          case 'chatbots':
            const chatbotsData = await fetchData('chatbots');
            setChatbots(chatbotsData || []);
            break;
          case 'tickets':
            const ticketsData = await fetchData('tickets');
            setTickets(ticketsData || []);
            break;
          case 'templates':
            const templatesData = await fetchData('templates');
            setTemplates(templatesData || []);
            break;
          case 'warmer':
            const warmerData = await fetchData('warmer');
            setWarmer(warmerData);
            break;
          case 'api':
            const keysData = await fetchData('api-keys');
            setApiKeys(keysData || []);
            const repliesData = await fetchData('quick-replies');
            setQuickReplies(repliesData || []);
            break;
          default:
            break;
        }
      } catch (err) {
        console.error('Error loading tab data:', err);
      }
    };
    loadTabData();
  }, [activeTab, fetchData, contactsQuery, selectedWebsiteId]);

  const handleConnect = async () => {
    setError(null);
    setConnecting(true);
    try {
      const phone = (settingsPhone || account?.phone_number || '').trim() || '+10000000000';
      const displayName = (settingsDisplayName || account?.display_name || '').trim() || 'My Business';
      const result = await postData('account/connect', {
        phone_number: phone,
        display_name: displayName,
        provider: 'auto',
      });
      setAccount((prev) => ({
        ...prev,
        ...result,
        status: result.status || 'connecting',
        qr_code: result.qr_code ?? prev?.qr_code,
        provider: result.provider ?? prev?.provider,
      }));
      if (!settingsPhone) setSettingsPhone(phone);
      if (!settingsDisplayName) setSettingsDisplayName(displayName);
    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e?.preventDefault?.();
    setSettingsSaving(true);
    setError(null);
    try {
      const updated = await postData('account/settings', {
        phone_number: settingsPhone.trim(),
        display_name: settingsDisplayName.trim(),
      });
      setAccount((prev) => ({ ...prev, ...updated }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleConfirmConnection = async () => {
    setError(null);
    try {
      const result = await postData('account/confirm-connection', {});
      if (result.status === 'connected') {
        setAccount((prev) => ({ ...prev, status: 'connected', qr_code: null }));
        setError(null);
      } else {
        setAccount((prev) => ({ ...prev, status: result.status || prev.status }));
        setError(result.message || 'Not connected yet — scan the QR and try again');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDisconnect = async () => {
    try {
      await postData('account/disconnect', {});
      setAccount(prev => ({ ...prev, status: 'disconnected' }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateContact = async (data) => {
    try {
      const newContact = await postData('contacts', {
        ...data,
        website_id: selectedWebsiteId || '',
      });
      setContacts(prev => [newContact, ...prev]);
      setShowCreateModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateBroadcast = async (data) => {
    try {
      const newBroadcast = await postData('broadcasts', data);
      setBroadcasts(prev => [newBroadcast, ...prev]);
      setShowCreateModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendBroadcast = async (broadcastId) => {
    if (account?.status !== 'connected') {
      setError('Connect WhatsApp before sending broadcasts');
      return;
    }
    try {
      const result = await postData(`broadcasts/${broadcastId}/send`, {});
      setBroadcasts((prev) => prev.map((b) => (b.id === broadcastId ? result : b)));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteBroadcast = async (broadcastId) => {
    try {
      await deleteData(`broadcasts/${broadcastId}`);
      setBroadcasts((prev) => prev.filter((b) => b.id !== broadcastId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateChatbot = async (data) => {
    try {
      const newBot = await postData('chatbots', data);
      setChatbots(prev => [newBot, ...prev]);
      setShowCreateModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleChatbot = async (chatbotId) => {
    try {
      const result = await postData(`chatbots/${chatbotId}/toggle`, {});
      setChatbots(prev => prev.map(c => c.id === chatbotId ? { ...c, is_active: result.is_active } : c));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;
    try {
      await postData('messages/send', {
        contact_id: selectedConversation.contact_id,
        content: messageInput,
        message_type: 'text'
      });
      setMessageInput('');
      // Refresh conversation
      const convData = await fetchData(`conversations/${selectedConversation.id}`);
      setSelectedConversation(convData.conversation);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStartWarmer = async () => {
    try {
      await postData('warmer/start', {});
      setWarmer(prev => ({ ...prev, status: 'warming' }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStopWarmer = async () => {
    try {
      await postData('warmer/stop', {});
      setWarmer(prev => ({ ...prev, status: 'idle' }));
    } catch (err) {
      setError(err.message);
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setSelectedItem(item);
    setShowCreateModal(true);
  };

  // Shared connection panel — shown on every tab until connected
  const renderConnectionPanel = ({ compact = false } = {}) => {
    if (!account || account.status === 'connected') return null;
    const qrSrc = qrImageSrc(account.qr_code);

    return (
      <div className={`glass rounded-2xl border border-green-500/20 ${compact ? 'p-4 mb-4' : 'p-6 mb-6'}`} data-testid="wa-connection-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-semibold ${compact ? 'text-sm' : 'text-lg'}`}>WhatsApp Connection</h3>
          <span className="flex items-center gap-2 text-red-400 text-xs">
            <WifiOff className="w-4 h-4" />
            {account.status === 'connecting' ? 'Waiting for scan' : 'Disconnected'}
          </span>
        </div>

        {account.status === 'disconnected' && (
          <div className={`text-center ${compact ? 'py-4' : 'py-8'}`}>
            <QrCode className={`mx-auto mb-3 text-zinc-400 ${compact ? 'w-10 h-10' : 'w-16 h-16'}`} />
            <p className="text-zinc-400 mb-3 text-sm">Connect WhatsApp to send broadcasts, chats, and automations.</p>
            <Button onClick={handleConnect} className="gap-2" disabled={connecting || !token}>
              <QrCode className="w-4 h-4" />
              {connecting ? 'Generating QR…' : 'Generate QR Code'}
            </Button>
            {!token && (
              <p className="text-xs text-amber-300/90 mt-2">Sign in to generate a QR code.</p>
            )}
          </div>
        )}

        {account.status === 'connecting' && (
          <div className={`text-center ${compact ? 'py-2' : 'py-6'}`}>
            {qrSrc ? (
              <div className="bg-white p-4 rounded-xl inline-block mb-3">
                <img src={qrSrc} alt="WhatsApp QR code" className={compact ? 'w-36 h-36' : 'w-48 h-48'} />
              </div>
            ) : (
              <div className="mb-3">
                <p className="text-sm text-zinc-400 mb-2">QR code not loaded yet.</p>
                <Button onClick={handleConnect} size="sm" className="gap-2" disabled={connecting}>
                  <RefreshCw className={`w-4 h-4 ${connecting ? 'animate-spin' : ''}`} /> Refresh QR
                </Button>
              </div>
            )}
            <p className="text-zinc-400 mb-1 text-sm">Scan with WhatsApp → Settings → Linked Devices → Link a Device</p>
            {account.provider === 'mock' && (
              <p className="text-xs text-amber-300/90 mb-3">Demo mode: after viewing the QR, click confirm below (no real scan required).</p>
            )}
            <Button onClick={handleConfirmConnection} variant="outline" className="gap-2" size="sm">
              <Check className="w-4 h-4" /> I&apos;ve Scanned the Code
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Render Dashboard Tab
  const renderDashboard = () => (
    <div className="space-y-6">
      {renderConnectionPanel()}

      {account?.status === 'connected' && (
        <div className="glass p-6 rounded-2xl flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <Phone className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="font-medium flex items-center gap-2">
                {account.display_name || 'My WhatsApp'}
                <span className="text-xs text-green-400 inline-flex items-center gap-1"><Wifi className="w-3 h-3" /> Connected</span>
              </p>
              <p className="text-sm text-zinc-400">{account.phone_number || 'Linked device'}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-2 text-red-400 border-red-400/30">
            <WifiOff className="w-4 h-4" /> Disconnect
          </Button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Contacts" value={analytics?.summary?.total_contacts || 0} />
        <StatCard icon={MessageCircle} label="Messages Today" value={analytics?.daily?.[0]?.messages_sent || 0} color="blue" />
        <StatCard icon={Bot} label="Active Chatbots" value={analytics?.summary?.active_chatbots || 0} color="green" />
        <StatCard icon={Ticket} label="Open Tickets" value={analytics?.summary?.open_tickets || 0} color="orange" />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => setActiveTab('broadcasts')}
          className="glass p-4 rounded-xl hover:border-violet-500/40 transition-all text-left"
        >
          <Send className="w-6 h-6 text-violet-400 mb-2" />
          <p className="font-medium">Send Broadcast</p>
          <p className="text-xs text-zinc-400">Message all contacts</p>
        </button>
        <button
          onClick={() => setActiveTab('chatbots')}
          className="glass p-4 rounded-xl hover:border-violet-500/40 transition-all text-left"
        >
          <Bot className="w-6 h-6 text-green-400 mb-2" />
          <p className="font-medium">Create Chatbot</p>
          <p className="text-xs text-zinc-400">Automate responses</p>
        </button>
        <button
          onClick={() => setActiveTab('contacts')}
          className="glass p-4 rounded-xl hover:border-violet-500/40 transition-all text-left"
        >
          <UserPlus className="w-6 h-6 text-blue-400 mb-2" />
          <p className="font-medium">Add Contact</p>
          <p className="text-xs text-zinc-400">Grow your phonebook</p>
        </button>
        <button
          onClick={() => setActiveTab('warmer')}
          className="glass p-4 rounded-xl hover:border-violet-500/40 transition-all text-left"
        >
          <Shield className="w-6 h-6 text-orange-400 mb-2" />
          <p className="font-medium">Number Warmer</p>
          <p className="text-xs text-zinc-400">Prevent bans</p>
        </button>
      </div>

      {/* Analytics Chart Placeholder */}
      <div className="glass p-6 rounded-2xl">
        <h3 className="text-lg font-semibold mb-4">Message Analytics (Last 7 Days)</h3>
        <div className="grid grid-cols-7 gap-2">
          {analytics?.daily?.slice().reverse().map((day, i) => (
            <div key={i} className="text-center">
              <div className="h-32 bg-violet-500/10 rounded-lg relative overflow-hidden">
                <div
                  className="absolute bottom-0 left-0 right-0 bg-violet-500 rounded-b-lg transition-all"
                  style={{ height: `${Math.min((day.messages_sent / 200) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-zinc-400 mt-2">{day.date.split('-')[2]}</p>
              <p className="text-xs font-medium">{day.messages_sent}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-8 mt-4 text-sm">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 bg-violet-500 rounded" /> Sent
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded" /> Delivered
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 bg-blue-500 rounded" /> Read
          </span>
        </div>
      </div>
    </div>
  );

  // Render Contacts Tab
  const renderContacts = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <Upload className="w-4 h-4" /> Import
          </Button>
          <Button onClick={() => openModal('contact')} className="gap-2">
            <Plus className="w-4 h-4" /> Add Contact
          </Button>
        </div>
      </div>

      {/* Groups */}
      {groups.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <span className="text-sm text-zinc-400">Groups:</span>
          {groups.map(group => (
            <button
              key={group.id}
              className="px-3 py-1 text-sm rounded-full bg-white/5 hover:bg-white/10 flex items-center gap-2"
              style={{ borderLeft: `3px solid ${group.color}` }}
            >
              {group.name} <span className="text-zinc-400">({group.contact_count})</span>
            </button>
          ))}
          <button
            onClick={() => openModal('group')}
            className="px-3 py-1 text-sm rounded-full border border-dashed border-zinc-600 hover:border-violet-500 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> New Group
          </button>
        </div>
      )}

      {/* Contacts List */}
      {contacts.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No contacts yet"
          description="Add contacts to start messaging and building your CRM"
          action="Add First Contact"
          onAction={() => openModal('contact')}
        />
      ) : (
        <div className="glass rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Contact</th>
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Phone</th>
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Tags</th>
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Status</th>
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Last Message</th>
                <th className="text-right p-4 text-sm text-zinc-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.filter(c => 
                c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                c.phone_number?.includes(searchQuery)
              ).map(contact => (
                <tr key={contact.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                        {contact.name?.[0]?.toUpperCase() || <Users className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="font-medium">{contact.name || 'Unknown'}</p>
                        <p className="text-xs text-zinc-400">{contact.email}</p>
                        {(contact.source === 'lead_pipeline' || contact.tags?.includes('lead-pipeline')) && (
                          <p className="text-[10px] text-emerald-300 mt-1" data-testid={`whatsapp-contact-sync-badge-${contact.id}`}>
                            Lead Scraper sync
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm">{contact.phone_number}</td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {contact.tags?.slice(0, 2).map(tag => (
                        <span key={tag} className="px-2 py-0.5 text-xs bg-violet-500/20 rounded-full">{tag}</span>
                      ))}
                      {contact.tags?.length > 2 && (
                        <span className="text-xs text-zinc-400">+{contact.tags.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      contact.status === 'active' ? 'bg-green-500/20 text-green-400' :
                      contact.status === 'opted_out' ? 'bg-red-500/20 text-red-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {contact.status}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-zinc-400">
                    {contact.last_message_at ? new Date(contact.last_message_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button className="p-2 hover:bg-white/10 rounded-lg" title="Send Message">
                        <MessageCircle className="w-4 h-4" />
                      </button>
                      <button className="p-2 hover:bg-white/10 rounded-lg" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button className="p-2 hover:bg-white/10 rounded-lg text-red-400" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Render Chats Tab
  const renderChats = () => (
    <div className="flex gap-4 h-[calc(100vh-280px)]">
      {/* Conversations List */}
      <div className="w-80 glass rounded-xl overflow-hidden flex flex-col">
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Search chats..."
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-violet-500"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-zinc-400">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No conversations yet</p>
            </div>
          ) : (
            conversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                className={`w-full p-4 text-left hover:bg-white/5 border-b border-white/5 ${
                  selectedConversation?.id === conv.id ? 'bg-violet-500/10' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                    {conv.contact_name?.[0]?.toUpperCase() || <Users className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium truncate">{conv.contact_name || conv.contact_phone}</p>
                      <span className="text-xs text-zinc-400">
                        {conv.last_message_at ? new Date(conv.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-zinc-400 truncate">{conv.last_message}</p>
                      {conv.unread_count > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-violet-500 rounded-full">{conv.unread_count}</span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Window */}
      <div className="flex-1 glass rounded-xl overflow-hidden flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                  {selectedConversation.contact_name?.[0]?.toUpperCase() || <Users className="w-4 h-4" />}
                </div>
                <div>
                  <p className="font-medium">{selectedConversation.contact_name || 'Unknown'}</p>
                  <p className="text-xs text-zinc-400">{selectedConversation.contact_phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-white/10 rounded-lg">
                  <Phone className="w-4 h-4" />
                </button>
                <button className="p-2 hover:bg-white/10 rounded-lg">
                  <Ticket className="w-4 h-4" />
                </button>
                <button className="p-2 hover:bg-white/10 rounded-lg">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 p-4 overflow-y-auto bg-black/20">
              <div className="text-center text-zinc-400 text-sm py-8">
                <p>This is the beginning of your conversation</p>
              </div>
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-white/10 rounded-lg">
                  <Plus className="w-5 h-5" />
                </button>
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                />
                <Button onClick={handleSendMessage} size="sm" className="gap-2">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-zinc-400">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Select a conversation to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render Settings Tab
  const renderSettings = () => (
    <div className="space-y-6 max-w-xl">
      <div>
        <h3 className="text-lg font-semibold">Account Settings</h3>
        <p className="text-sm text-zinc-400">Phone and display name used when linking WhatsApp.</p>
      </div>
      <form onSubmit={handleSaveSettings} className="glass p-6 rounded-2xl space-y-4">
        <div>
          <label className="block text-sm text-zinc-400 mb-1">Business phone (E.164)</label>
          <input
            type="tel"
            value={settingsPhone}
            onChange={(e) => setSettingsPhone(e.target.value)}
            placeholder="+15551234567"
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-1">Display name</label>
          <input
            type="text"
            value={settingsDisplayName}
            onChange={(e) => setSettingsDisplayName(e.target.value)}
            placeholder="My Business"
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-green-500"
          />
        </div>
        <p className="text-xs text-zinc-500">
          Provider: <span className="text-zinc-300">{account?.provider || 'auto'}</span>
          {account?.provider === 'mock' && ' — local demo mode (Evolution API not configured)'}
        </p>
        <Button type="submit" disabled={settingsSaving} className="gap-2">
          {settingsSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
          Save settings
        </Button>
      </form>
      {renderConnectionPanel()}
    </div>
  );

  // Render Broadcasts Tab
  const renderBroadcasts = () => (
    <div className="space-y-4">
      {renderConnectionPanel({ compact: true })}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Broadcast Campaigns</h3>
          <p className="text-sm text-zinc-400">Send messages to multiple contacts at once</p>
        </div>
        <Button onClick={() => openModal('broadcast')} className="gap-2">
          <Plus className="w-4 h-4" /> New Broadcast
        </Button>
      </div>

      {broadcasts.length === 0 ? (
        <EmptyState
          icon={Send}
          title="No broadcasts yet"
          description="Create your first broadcast to message multiple contacts"
          action="Create Broadcast"
          onAction={() => openModal('broadcast')}
        />
      ) : (
        <div className="grid gap-4">
          {broadcasts.map(broadcast => (
            <div key={broadcast.id} data-broadcast-id={broadcast.id} className="glass p-4 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium">{broadcast.name}</h4>
                  <p className="text-sm text-zinc-400">
                    {broadcast.total_recipients} recipients • Created {new Date(broadcast.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-3 py-1 text-sm rounded-full ${
                  broadcast.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                  broadcast.status === 'running' ? 'bg-blue-500/20 text-blue-400' :
                  broadcast.status === 'scheduled' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-zinc-500/20 text-zinc-400'
                }`}>
                  {broadcast.status}
                </span>
              </div>

              <div className="bg-white/5 p-3 rounded-lg mb-4">
                <p className="text-sm">{broadcast.content}</p>
              </div>

              {broadcast.status === 'completed' && (
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-400">{broadcast.sent_count}</p>
                    <p className="text-xs text-zinc-400">Sent</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-400">{broadcast.delivered_count}</p>
                    <p className="text-xs text-zinc-400">Delivered</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-violet-400">{broadcast.read_count}</p>
                    <p className="text-xs text-zinc-400">Read</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-400">{broadcast.failed_count}</p>
                    <p className="text-xs text-zinc-400">Failed</p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                {['draft', 'scheduled'].includes(broadcast.status) && (
                  <Button
                    onClick={() => handleSendBroadcast(broadcast.id)}
                    size="sm"
                    className="gap-2"
                    disabled={account?.status !== 'connected'}
                    title={account?.status !== 'connected' ? 'Connect WhatsApp first' : undefined}
                  >
                    <Play className="w-4 h-4" /> Send Now
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 text-red-400 border-red-400/30"
                  onClick={() => handleDeleteBroadcast(broadcast.id)}
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render Chatbots Tab
  const renderChatbots = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Chatbot Automation</h3>
          <p className="text-sm text-zinc-400">Build automated response flows</p>
        </div>
        <Button onClick={() => openModal('chatbot')} className="gap-2">
          <Plus className="w-4 h-4" /> Create Chatbot
        </Button>
      </div>

      {chatbots.length === 0 ? (
        <EmptyState
          icon={Bot}
          title="No chatbots yet"
          description="Create automated chatbots to handle customer inquiries"
          action="Create Chatbot"
          onAction={() => openModal('chatbot')}
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {chatbots.map(bot => (
            <div key={bot.id} className="glass p-4 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${bot.is_active ? 'bg-green-500/20' : 'bg-zinc-500/20'}`}>
                    <Bot className={`w-5 h-5 ${bot.is_active ? 'text-green-400' : 'text-zinc-400'}`} />
                  </div>
                  <div>
                    <h4 className="font-medium">{bot.name}</h4>
                    <p className="text-xs text-zinc-400">{bot.nodes?.length || 0} nodes</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggleChatbot(bot.id)}
                  className={`px-3 py-1 text-sm rounded-full ${
                    bot.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
                  }`}
                >
                  {bot.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>

              <p className="text-sm text-zinc-400 mb-4">{bot.description || 'No description'}</p>

              <div className="flex flex-wrap gap-2 mb-4">
                {bot.trigger_keywords?.map(kw => (
                  <span key={kw} className="px-2 py-1 text-xs bg-violet-500/20 rounded-full">{kw}</span>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 text-center">
                <div>
                  <p className="text-xl font-bold">{bot.sessions_started}</p>
                  <p className="text-xs text-zinc-400">Sessions Started</p>
                </div>
                <div>
                  <p className="text-xl font-bold">{bot.sessions_completed}</p>
                  <p className="text-xs text-zinc-400">Completed</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="flex-1 gap-2">
                  <Edit2 className="w-4 h-4" /> Edit Flow
                </Button>
                <button className="p-2 hover:bg-white/10 rounded-lg text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render Tickets Tab
  const renderTickets = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Support Tickets</h3>
          <p className="text-sm text-zinc-400">Manage customer support requests</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm">
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {tickets.length === 0 ? (
        <EmptyState
          icon={Ticket}
          title="No tickets yet"
          description="Support tickets will appear here when customers need help"
        />
      ) : (
        <div className="glass rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Ticket</th>
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Subject</th>
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Priority</th>
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Status</th>
                <th className="text-left p-4 text-sm text-zinc-400 font-medium">Created</th>
                <th className="text-right p-4 text-sm text-zinc-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(ticket => (
                <tr key={ticket.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <span className="font-mono text-sm">{ticket.ticket_number}</span>
                  </td>
                  <td className="p-4">
                    <p className="font-medium">{ticket.subject}</p>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      ticket.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                      ticket.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                      ticket.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      ticket.status === 'open' ? 'bg-blue-500/20 text-blue-400' :
                      ticket.status === 'in_progress' ? 'bg-violet-500/20 text-violet-400' :
                      ticket.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {ticket.status}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-zinc-400">
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm">View</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Render Templates Tab
  const renderTemplates = () => {
    const { funnels: waFunnels } = groupTemplatesByFunnel(templates);
    const bodyText = (t) => t.components?.find((c) => c.type === 'BODY')?.text || '';

    const openBroadcastFromTemplate = (template) => {
      setActiveTab('broadcasts');
      setModalType('broadcast');
      setSelectedItem({
        name: template.funnel_name ? `${template.funnel_name} — ${template.name}` : template.name,
        content: bodyText(template),
      });
      setShowCreateModal(true);
    };

    return (
    <div className="space-y-4">
      {waFunnels.length > 0 && (
        <section className="glass p-4 rounded-xl border border-green-500/20" data-testid="wa-funnel-library">
          <h3 className="text-sm font-semibold mb-1">Starter WhatsApp funnels</h3>
          <p className="text-xs text-zinc-400 mb-4">Welcome, support, promo, appointment, and feedback flows.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {waFunnels.map((funnel) => (
              <div key={funnel.slug} className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/40">
                <p className="text-sm font-medium text-green-300">{funnel.name}</p>
                <p className="text-xs text-zinc-500 mb-2">{funnel.description}</p>
                <ul className="space-y-2">
                  {funnel.steps.map((step) => (
                    <li key={step.id} className="text-xs border border-zinc-800 rounded p-2">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span>Step {step.funnel_step}/{step.funnel_step_total} — {step.name}</span>
                        <button type="button" onClick={() => openBroadcastFromTemplate(step)} className="text-green-300 hover:text-green-100">Use</button>
                      </div>
                      <p className="text-zinc-400">{bodyText(step)}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Message Templates</h3>
          <p className="text-sm text-zinc-400">Pre-approved templates for broadcasting</p>
        </div>
        <Button onClick={() => openModal('template')} className="gap-2">
          <Plus className="w-4 h-4" /> Create Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No templates yet"
          description="Create message templates for consistent communication"
          action="Create Template"
          onAction={() => openModal('template')}
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => (
            <div key={template.id} className="glass p-4 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">{template.name}</h4>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  template.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                  template.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {template.status}
                </span>
              </div>
              <div className="bg-white/5 p-3 rounded-lg mb-3 text-sm">
                {template.components?.find(c => c.type === 'BODY')?.text || 'No content'}
              </div>
              <div className="flex items-center justify-between text-xs text-zinc-400">
                <span>{template.category}</span>
                <span>Used {template.usage_count} times</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  // Render Warmer Tab
  const renderWarmer = () => (
    <div className="space-y-6">
      <div className="glass p-6 rounded-2xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">WhatsApp Number Warmer</h3>
            <p className="text-sm text-zinc-400">Warm your number to reduce ban risk</p>
          </div>
          {warmer?.status === 'warming' ? (
            <Button onClick={handleStopWarmer} variant="outline" className="gap-2 text-red-400">
              <Pause className="w-4 h-4" /> Stop Warmer
            </Button>
          ) : (
            <Button onClick={handleStartWarmer} className="gap-2">
              <Play className="w-4 h-4" /> Start Warmer
            </Button>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-white/5 rounded-xl">
            <Shield className={`w-8 h-8 mx-auto mb-2 ${warmer?.status === 'warming' ? 'text-green-400' : 'text-zinc-400'}`} />
            <p className="font-medium capitalize">{warmer?.status || 'Idle'}</p>
            <p className="text-xs text-zinc-400">Current Status</p>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-xl">
            <p className="text-2xl font-bold">{warmer?.current_day_sent || 0}/{warmer?.messages_per_day || 10}</p>
            <p className="text-xs text-zinc-400">Messages Today</p>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-xl">
            <p className="text-2xl font-bold">{warmer?.total_sent || 0}</p>
            <p className="text-xs text-zinc-400">Total Messages Sent</p>
          </div>
        </div>
      </div>

      <div className="glass p-6 rounded-2xl">
        <h4 className="font-medium mb-4">Warmer Settings</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Target Phone Number</label>
            <input
              type="text"
              placeholder="+1234567890"
              value={warmer?.target_phone || ''}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
            />
            <p className="text-xs text-zinc-400 mt-1">The phone number that will receive warming messages</p>
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Messages Per Day</label>
            <input
              type="number"
              min="1"
              max="50"
              value={warmer?.messages_per_day || 10}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
            />
          </div>
        </div>
      </div>

      <div className="glass p-6 rounded-2xl">
        <h4 className="font-medium mb-4">Warming Messages</h4>
        <div className="space-y-3">
          {warmer?.messages?.map((msg, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              <span className="text-sm text-zinc-400">{i + 1}.</span>
              <p className="flex-1 text-sm">{msg.content}</p>
              <span className="text-xs text-zinc-400">Delay: {msg.delay_minutes}min</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render API Tab
  const renderAPI = () => (
    <div className="space-y-6">
      <div className="glass p-6 rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">API Keys</h3>
            <p className="text-sm text-zinc-400">Manage API access for external integrations</p>
          </div>
          <Button onClick={() => postData('api-keys', { name: 'New API Key' }).then(key => setApiKeys([...apiKeys, key]))} className="gap-2">
            <Plus className="w-4 h-4" /> Generate Key
          </Button>
        </div>

        {apiKeys.length === 0 ? (
          <div className="text-center py-8 text-zinc-400">
            <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No API keys yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {apiKeys.map(key => (
              <div key={key.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <p className="font-medium">{key.name}</p>
                  <p className="font-mono text-sm text-zinc-400">{key.key}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigator.clipboard.writeText(key.key)}
                    className="p-2 hover:bg-white/10 rounded-lg"
                    title="Copy"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteData(`api-keys/${key.id}`).then(() => setApiKeys(apiKeys.filter(k => k.id !== key.id)))}
                    className="p-2 hover:bg-white/10 rounded-lg text-red-400"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass p-6 rounded-2xl">
        <h3 className="text-lg font-semibold mb-4">Quick Replies</h3>
        <div className="space-y-3">
          {quickReplies.map(reply => (
            <div key={reply.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div>
                <p className="font-medium">{reply.title}</p>
                <p className="text-sm text-zinc-400">Shortcut: <code className="bg-white/10 px-2 py-0.5 rounded">{reply.shortcut}</code></p>
              </div>
              <button className="p-2 hover:bg-white/10 rounded-lg text-red-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button variant="outline" className="w-full gap-2">
            <Plus className="w-4 h-4" /> Add Quick Reply
          </Button>
        </div>
      </div>

      <div className="glass p-6 rounded-2xl">
        <h3 className="text-lg font-semibold mb-4">API Documentation</h3>
        <div className="space-y-4">
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="font-medium mb-2">Send Message</p>
            <code className="text-sm text-green-400">POST /api/whatsapp/messages/send</code>
            <pre className="mt-2 p-3 bg-black/30 rounded text-xs overflow-x-auto">
{`{
  "contact_id": "string",
  "content": "Hello!",
  "message_type": "text"
}`}
            </pre>
          </div>
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="font-medium mb-2">Get Contacts</p>
            <code className="text-sm text-blue-400">GET /api/whatsapp/contacts</code>
          </div>
          <div className="p-4 bg-white/5 rounded-lg">
            <p className="font-medium mb-2">Create Broadcast</p>
            <code className="text-sm text-green-400">POST /api/whatsapp/broadcasts</code>
          </div>
        </div>
        <Button variant="outline" className="w-full mt-4 gap-2">
          <ExternalLink className="w-4 h-4" /> View Full Documentation
        </Button>
      </div>
    </div>
  );

  // Create Modal
  const renderModal = () => {
    if (!showCreateModal) return null;

    const handleSubmit = (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData.entries());

      switch (modalType) {
        case 'contact':
          handleCreateContact({
            phone_number: data.phone_number,
            name: data.name,
            email: data.email || '',
            tags: data.tags ? data.tags.split(',').map(t => t.trim()) : [],
            groups: [],
            notes: data.notes || ''
          });
          break;
        case 'broadcast':
          handleCreateBroadcast({
            name: data.name,
            content: data.content,
            message_type: 'text',
            target_type: data.target_type || 'all'
          });
          break;
        case 'chatbot':
          handleCreateChatbot({
            name: data.name,
            description: data.description || '',
            trigger_keywords: data.keywords ? data.keywords.split(',').map(k => k.trim()) : []
          });
          break;
        default:
          break;
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="glass rounded-2xl w-full max-w-md">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {modalType === 'contact' && 'Add Contact'}
              {modalType === 'broadcast' && 'Create Broadcast'}
              {modalType === 'chatbot' && 'Create Chatbot'}
              {modalType === 'group' && 'Create Group'}
              {modalType === 'template' && 'Create Template'}
            </h3>
            <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {modalType === 'contact' && (
              <>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Phone Number *</label>
                  <input
                    name="phone_number"
                    type="tel"
                    required
                    placeholder="+1234567890"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Name</label>
                  <input
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Email</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Tags (comma-separated)</label>
                  <input
                    name="tags"
                    type="text"
                    placeholder="customer, vip, newsletter"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  />
                </div>
              </>
            )}

            {modalType === 'broadcast' && (
              <>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Campaign Name *</label>
                  <input
                    name="name"
                    type="text"
                    required
                    key={`broadcast-name-${selectedItem?.name || 'empty'}`}
                    defaultValue={selectedItem?.name || ''}
                    placeholder="Holiday Sale Announcement"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Message *</label>
                  <textarea
                    name="content"
                    required
                    rows={4}
                    key={`broadcast-content-${selectedItem?.content || 'empty'}`}
                    defaultValue={selectedItem?.content || ''}
                    placeholder="Type your message here..."
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Send To</label>
                  <select
                    name="target_type"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  >
                    <option value="all">All Contacts</option>
                    <option value="groups">Specific Groups</option>
                    <option value="tags">By Tags</option>
                  </select>
                </div>
              </>
            )}

            {modalType === 'chatbot' && (
              <>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Chatbot Name *</label>
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder="Support Bot"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Description</label>
                  <textarea
                    name="description"
                    rows={2}
                    placeholder="Handles customer support queries"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Trigger Keywords (comma-separated)</label>
                  <input
                    name="keywords"
                    type="text"
                    placeholder="hello, hi, help, support"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-violet-500"
                  />
                </div>
              </>
            )}

            <div className="flex items-center gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                Create
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/marketing-suite')}
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-green-400" />
              WhatsApp Marketing
            </h1>
            <p className="text-sm text-zinc-400">Manage your WhatsApp CRM, broadcasts, and automation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Workspace</span>
          <select
            value={selectedWebsiteId}
            onChange={(e) => setSelectedWebsiteId(e.target.value)}
            className="h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-green-500"
            data-testid="whatsapp-website-select"
          >
            <option value="">Account-wide</option>
            {websites.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
          <Button variant="ghost" size="icon" onClick={loadWebsites} disabled={websitesLoading} className="h-8 w-8">
            <RefreshCw className={`w-4 h-4 ${websitesLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setActiveTab('settings')}>
            <Settings className="w-4 h-4" /> Settings
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        <TabButton active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} icon={BarChart3} label="Dashboard" />
        <TabButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={Settings} label="Settings" />
        <TabButton active={activeTab === 'contacts'} onClick={() => setActiveTab('contacts')} icon={Users} label="Contacts" />
        <TabButton active={activeTab === 'chats'} onClick={() => setActiveTab('chats')} icon={MessageSquare} label="Chats" badge={0} />
        <TabButton active={activeTab === 'broadcasts'} onClick={() => setActiveTab('broadcasts')} icon={Send} label="Broadcasts" />
        <TabButton active={activeTab === 'chatbots'} onClick={() => setActiveTab('chatbots')} icon={Bot} label="Chatbots" />
        <TabButton active={activeTab === 'tickets'} onClick={() => setActiveTab('tickets')} icon={Ticket} label="Tickets" />
        <TabButton active={activeTab === 'templates'} onClick={() => setActiveTab('templates')} icon={FileText} label="Templates" />
        <TabButton active={activeTab === 'warmer'} onClick={() => setActiveTab('warmer')} icon={Shield} label="Warmer" />
        <TabButton active={activeTab === 'api'} onClick={() => setActiveTab('api')} icon={Zap} label="API" />
      </div>

      {/* Tab Content */}
      <div>
        {account?.status !== 'connected' && activeTab !== 'dashboard' && activeTab !== 'settings' && activeTab !== 'broadcasts' && renderConnectionPanel({ compact: true })}
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'settings' && renderSettings()}
        {activeTab === 'contacts' && renderContacts()}
        {activeTab === 'chats' && renderChats()}
        {activeTab === 'broadcasts' && renderBroadcasts()}
        {activeTab === 'chatbots' && renderChatbots()}
        {activeTab === 'tickets' && renderTickets()}
        {activeTab === 'templates' && renderTemplates()}
        {activeTab === 'warmer' && renderWarmer()}
        {activeTab === 'api' && renderAPI()}
      </div>

      {/* Modal */}
      {renderModal()}
    </div>
  );
};

export default WhatsAppMarketingDashboard;
